// src/pages/Admin/Recintos/Detalle.jsx
//
// Antes el estado y el tipo se repetían tres veces (cabecera, tarjetas y tabla de
// datos) y los talleres —lo que más se consulta— quedaban al fondo. Ahora: cabecera
// con acciones → resumen de talleres que filtra → tabla de talleres + ficha lateral.
import React, { useState, useEffect } from "react";
import { Spin, App, Table, Input, Dropdown, Tooltip } from "antd";
import { DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import {
  FiArrowLeft, FiMapPin, FiEdit2, FiPower, FiMoreVertical, FiGrid, FiCheckCircle,
  FiPauseCircle, FiUnlock, FiSearch, FiChevronRight, FiCopy, FiUser, FiCalendar, FiInfo,
} from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";
import Recintos from "../../../../service/Recintos";
import { useNotify, useWithNotify } from "../../../../providers/NotificationProvider";
import { ChipEstado, ChipNeutro, TarjetaResumen, FiltroChips, claseBtnPrimario, claseBtnSecundario } from "../../../Ui/Admin/PiezasAdmin";
import { formatearNombre } from "../../../../utils/texto";
import dayjs from "dayjs";
import "dayjs/locale/es";

dayjs.locale("es");

const TIPOS = {
  polideportivo: "Polideportivo", gimnasio: "Gimnasio", piscina: "Piscina", cancha: "Cancha",
  sala: "Sala", otro: "Otro", calle_avenida: "Calle o Avenida", colegio_liceo: "Colegio o Liceo",
  multicancha: "Multicancha", parque: "Parque", pista_atletica: "Pista Atlética", plaza: "Plaza",
  sede_vecinal: "Sede Vecinal", skate_park: "Skate Park",
};

const INSCRIPCIONES = {
  abiertas: { label: "Abiertas", cls: "bg-secondary/40 text-[#5c4300]" },
  proximamente: { label: "Próximamente", cls: "bg-blue-50 text-primary" },
  pausadas: { label: "Pausadas", cls: "bg-gray-100 text-gray-500" },
  cerradas: { label: "Cerradas", cls: "bg-gray-100 text-gray-500" },
};

const direccionReal = (d) => (d && !/^sin direcci[oó]n$/i.test(d.trim()) ? d : null);

const Tarjeta = ({ children, className = "" }) => (
  <section className={`rounded-3xl bg-white border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,.04),0_8px_24px_-14px_rgba(0,0,0,.10)] ${className}`}>
    {children}
  </section>
);

const Dato = ({ icon, label, children }) => (
  <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
    <span className="w-8 h-8 flex-shrink-0 rounded-lg bg-blue-50 text-primary flex items-center justify-center">{icon}</span>
    <div className="min-w-0">
      <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 m-0">{label}</p>
      <div className="text-sm text-gray-900 mt-0.5 break-words">{children}</div>
    </div>
  </div>
);

export default function DetalleRecinto() {
  const [recinto, setRecinto] = useState(null);
  const [talleres, setTalleres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTalleres, setLoadingTalleres] = useState(true);
  const [filtro, setFiltro] = useState("todos");
  const [busqueda, setBusqueda] = useState("");

  const navigate = useNavigate();
  const { id } = useParams();
  const notify = useNotify();
  const withNotify = useWithNotify();
  const { modal } = App.useApp();

  useEffect(() => {
    loadRecinto();
    loadTalleres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadRecinto = async () => {
    try {
      setLoading(true);
      setRecinto(await Recintos.GetById(id));
    } catch (error) {
      console.error("Error cargando recinto:", error);
      notify.error("No se pudo cargar el recinto");
      navigate("/admin/recintos");
    } finally {
      setLoading(false);
    }
  };

  const loadTalleres = async () => {
    try {
      setLoadingTalleres(true);
      setTalleres((await Recintos.GetTalleres(id)) || []);
    } catch (error) {
      console.error("Error cargando talleres:", error);
    } finally {
      setLoadingTalleres(false);
    }
  };

  const handleDelete = () => {
    modal.confirm({
      title: "¿Eliminar recinto?",
      icon: <ExclamationCircleOutlined />,
      content: `¿Estás seguro de eliminar el recinto "${recinto.nombre}"? Esta acción no se puede deshacer.`,
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await withNotify(Recintos.Delete(id), {
            loading: "Eliminando recinto...",
            success: "Recinto eliminado exitosamente",
            error: "No se pudo eliminar el recinto",
          });
          navigate("/admin/recintos");
        } catch (error) {
          console.error("Error eliminando recinto:", error);
        }
      },
    });
  };

  const handleCambiarEstado = async () => {
    const nuevoEstado = recinto.estado === "activo" ? "inactivo" : "activo";
    try {
      await withNotify(Recintos.CambiarEstado(id, nuevoEstado), {
        loading: `Cambiando estado a ${nuevoEstado}...`,
        success: `Recinto ${nuevoEstado === "activo" ? "activado" : "desactivado"} exitosamente`,
        error: "No se pudo cambiar el estado",
      });
      loadRecinto();
    } catch (error) {
      console.error("Error cambiando estado:", error);
    }
  };

  const copiarId = async () => {
    try { await navigator.clipboard.writeText(recinto._id); notify.success("ID copiado"); }
    catch { notify.error("No se pudo copiar"); }
  };

  if (loading) {
    return <div className="py-24 text-center"><Spin size="large" /></div>;
  }

  if (!recinto) {
    return (
      <div className="py-24 text-center">
        <p className="font-bold text-gray-900">Recinto no encontrado</p>
        <Link to="/admin/recintos" className="text-sm font-bold text-primary">Volver a recintos</Link>
      </div>
    );
  }

  const activo = recinto.estado === "activo";
  const dir = direccionReal(recinto.direccion);
  const nActivos = talleres.filter(t => t.estado === "activo").length;
  const nAbiertas = talleres.filter(t => t.inscripcionesEstado === "abiertas").length;

  const talleresFiltrados = talleres
    .filter(t => filtro === "todos" ? true
      : filtro === "activos" ? t.estado === "activo"
        : filtro === "pausados" ? t.estado !== "activo"
          : t.inscripcionesEstado === "abiertas")
    .filter(t => !busqueda || (t.nombre || "").toLowerCase().includes(busqueda.toLowerCase()));

  const columnsTalleres = [
    {
      title: "Taller",
      dataIndex: "nombre",
      key: "nombre",
      render: (text, t) => (
        <div className="min-w-0">
          <div className="font-bold text-gray-900 truncate">{text}</div>
          {t.tipoActividad && <div className="text-xs text-gray-500 truncate">{t.tipoActividad}</div>}
        </div>
      ),
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      width: 120,
      render: (estado) => <ChipEstado activo={estado === "activo"} labelInactivo="Pausado" />,
    },
    {
      title: "Inscripciones",
      dataIndex: "inscripcionesEstado",
      key: "inscripcionesEstado",
      width: 140,
      render: (estado) => {
        const e = INSCRIPCIONES[estado] || INSCRIPCIONES.cerradas;
        return <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${e.cls}`}>{e.label}</span>;
      },
    },
    {
      title: "Ventana",
      key: "ventana",
      width: 170,
      render: (_, t) => t.inscripcionInicio && t.inscripcionFin
        ? <span className="text-xs text-gray-700 tabular-nums">{dayjs(t.inscripcionInicio).format("DD/MM/YY")} – {dayjs(t.inscripcionFin).format("DD/MM/YY")}</span>
        : <span className="text-xs text-gray-400">Sin ventana</span>,
    },
    {
      title: <span className="sr-only">Abrir</span>,
      key: "abrir",
      width: 40,
      render: () => <FiChevronRight className="text-gray-300" />,
    },
  ];

  return (
    <div className="max-w-[1280px]">
      <Link to="/admin/recintos"
        className="inline-flex items-center gap-1.5 mb-4 text-sm font-semibold text-gray-500 hover:!text-primary transition-colors">
        <FiArrowLeft size={15} /> Recintos
      </Link>

      {/* ── Cabecera ─────────────────────────────────────────────────── */}
      <Tarjeta className="p-6 mb-5">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-5">
          <div className="flex items-start gap-4 min-w-0">
            <span className={`relative w-14 h-14 flex-shrink-0 rounded-2xl flex items-center justify-center
              ${activo ? "bg-primary text-white" : "bg-gray-100 text-gray-400"}`}>
              <FiMapPin size={24} />
              {activo && <span aria-hidden className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-secondary ring-4 ring-white" />}
            </span>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight m-0">{formatearNombre(recinto.nombre)}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <ChipEstado activo={activo} />
                <ChipNeutro>{TIPOS[recinto.tipo] || TIPOS.otro}</ChipNeutro>
                <span className={`inline-flex items-center gap-1 text-sm ${dir ? "text-gray-600" : "text-gray-400 italic"}`}>
                  <FiMapPin size={13} /> {dir ? `${dir}${recinto.comuna ? `, ${recinto.comuna}` : ""}` : "Sin dirección"}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            <button type="button" onClick={handleCambiarEstado} className={claseBtnSecundario}>
              <FiPower size={15} /> {activo ? "Desactivar" : "Activar"}
            </button>
            <button type="button" onClick={() => navigate(`/admin/recintos/${id}/editar`)} className={claseBtnPrimario}>
              <FiEdit2 size={15} /> Editar
            </button>
            <Dropdown trigger={["click"]} placement="bottomRight" menu={{
              items: [{
                key: "eliminar", danger: true, icon: <DeleteOutlined />, disabled: activo,
                label: activo ? "Eliminar (desactívalo primero)" : "Eliminar recinto",
                onClick: handleDelete,
              }],
            }}>
              <button type="button" aria-label="Más acciones"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:border-primary hover:text-primary transition-colors">
                <FiMoreVertical size={17} />
              </button>
            </Dropdown>
          </div>
        </div>
      </Tarjeta>

      {/* ── Resumen de talleres (filtra la tabla) ───────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <TarjetaResumen icon={<FiGrid size={19} />} label="Talleres" value={talleres.length}
          activa={filtro === "todos"} onClick={() => setFiltro("todos")} />
        <TarjetaResumen icon={<FiCheckCircle size={19} />} label="Activos" value={nActivos}
          activa={filtro === "activos"} onClick={() => setFiltro("activos")} />
        <TarjetaResumen icon={<FiPauseCircle size={19} />} label="Pausados" value={talleres.length - nActivos}
          activa={filtro === "pausados"} onClick={() => setFiltro("pausados")} />
        <TarjetaResumen icon={<FiUnlock size={19} />} label="Inscripciones abiertas" value={nAbiertas}
          activa={filtro === "abiertas"} onClick={() => setFiltro("abiertas")} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-5 items-start">
        {/* ── Talleres ──────────────────────────────────────────────── */}
        <Tarjeta className="overflow-hidden">
          <header className="flex flex-wrap items-center justify-between gap-3 px-6 pt-5 pb-4">
            <h2 className="flex items-center gap-2 text-lg font-extrabold text-gray-900 m-0">
              Talleres <span className="hl-amarillo">del recinto</span>
              <Tooltip title="Los talleres funcionan con suscripciones individuales: cada persona inicia su período el día que paga, según la duración configurada.">
                <FiInfo size={15} className="text-gray-400 cursor-help" aria-label="Cómo funcionan las suscripciones" />
              </Tooltip>
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <Input allowClear prefix={<FiSearch className="text-gray-400" />} placeholder="Buscar taller…"
                value={busqueda} onChange={e => setBusqueda(e.target.value)} className="!rounded-full"
                style={{ width: 220 }} aria-label="Buscar taller" />
              <FiltroChips label="Filtrar talleres" valor={filtro} onChange={setFiltro} opciones={[
                { value: "todos", label: "Todos" },
                { value: "activos", label: "Activos" },
                { value: "pausados", label: "Pausados" },
              ]} />
            </div>
          </header>
          <div className="admin-table-card !rounded-none !border-0 !border-t !border-gray-100">
            <Table
              columns={columnsTalleres}
              dataSource={talleresFiltrados}
              loading={loadingTalleres}
              rowKey="_id"
              rowClassName={() => "fila-clicable"}
              onRow={(t) => ({ onClick: () => navigate(`/admin/talleres/editar/${t._id}`) })}
              pagination={talleresFiltrados.length > 10 ? { pageSize: 10, style: { padding: "12px 16px" } } : false}
              scroll={{ x: 640 }}
              locale={{
                emptyText: (
                  <div className="py-10 text-center">
                    <p className="font-bold text-gray-900 m-0">
                      {talleres.length === 0 ? "Este recinto aún no tiene talleres" : "Ningún taller coincide"}
                    </p>
                    <p className="text-sm text-gray-500 m-0 mt-1">
                      {talleres.length === 0 ? "Cuando se cree un taller aquí, aparecerá en esta lista." : "Prueba con otra búsqueda o filtro."}
                    </p>
                  </div>
                ),
              }}
            />
          </div>
        </Tarjeta>

        {/* ── Ficha lateral ─────────────────────────────────────────── */}
        <div className="space-y-5">
          <Tarjeta className="px-5 pt-4 pb-2">
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-gray-900 mb-1">Información</h2>
            <Dato icon={<FiMapPin size={14} />} label="Dirección">
              {dir || <span className="text-gray-400 italic">Sin dirección</span>}
              {recinto.comuna && <span className="block text-xs text-gray-500">{recinto.comuna}</span>}
            </Dato>
            <Dato icon={<FiInfo size={14} />} label="Descripción">
              {recinto.descripcion || <span className="text-gray-400 italic">Sin descripción</span>}
            </Dato>
          </Tarjeta>

          <Tarjeta className="px-5 pt-4 pb-2">
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-gray-900 mb-1">Registro</h2>
            <Dato icon={<FiUser size={14} />} label="Creado por">
              {recinto.creadoPorNombre || "—"}
              {recinto.creadoPorRol && <span className="ml-1.5 text-xs text-gray-500">({recinto.creadoPorRol})</span>}
              <span className="block text-xs text-gray-500">{dayjs(recinto.creadoEn).format("DD/MM/YYYY HH:mm")}</span>
            </Dato>
            {recinto.modificadoPor && (
              <Dato icon={<FiCalendar size={14} />} label="Última modificación">
                {recinto.modificadoPorNombre || "—"}
                {recinto.modificadoPorRol && <span className="ml-1.5 text-xs text-gray-500">({recinto.modificadoPorRol})</span>}
                <span className="block text-xs text-gray-500">{dayjs(recinto.modificadoEn).format("DD/MM/YYYY HH:mm")}</span>
              </Dato>
            )}
            <Dato icon={<FiCopy size={14} />} label="ID del recinto">
              <button type="button" onClick={copiarId} title="Copiar ID"
                className="inline-flex items-center gap-1.5 font-mono text-xs text-gray-600 hover:text-primary break-all text-left">
                {recinto._id} <FiCopy size={12} className="flex-shrink-0" />
              </button>
            </Dato>
          </Tarjeta>
        </div>
      </div>
    </div>
  );
}
